/**
 * A chase camera with a boat's manners.
 *
 * It follows position firmly but heading loosely: the tug can yaw quickly
 * under full helm while the camera swings round at its own pace, so the world
 * stays readable instead of whipping. The look target leads the tow slightly
 * when a barge is on the wire, keeping both vessels in frame.
 */

import { PerspectiveCamera, Vector3, type Quaternion } from 'three'
import { sampleWaterHeight } from '../world/waves.ts'

const _desired = new Vector3()
const _look = new Vector3()
const _forward = new Vector3()

export class ChaseCamera {
  readonly camera: PerspectiveCamera

  distance = 42
  height = 16
  /** How quickly the boom swings to follow the boat's heading, per second. */
  headingLag = 1.4

  /**
   * VIEW stops: close astern, onboard looking forward, working view,
   * full-tow view (the whole 220 m barge), and a harbour overview.
   */
  private static readonly ZOOMS: readonly { distance: number; height: number }[] = [
    { distance: 42, height: 16 },
    { distance: 85, height: 34 },
    // The far stops climb steeply: a 220 m tow occupies the water 45-265 m
    // astern, so a camera at deck height there would be inside the barge.
    { distance: 160, height: 115 },
    { distance: 280, height: 230 },
  ]
  private viewIndex = 0
  private readonly forwardView = new Vector3(0, 4.8, 4.8)
  private snapNextUpdate = false
  private largeVessel = false
  private vesselScale = 1
  private onboardPitch = 0

  get isOnboard(): boolean { return this.viewIndex === 1 }
  get viewName(): string { return ['Chase', 'Onboard', 'Wide', 'Tow', 'Overview'][this.viewIndex] }

  setVesselLength(length: number, forwardView?: Vector3): void {
    this.largeVessel = length > 50
    this.vesselScale = this.largeVessel ? Math.max(1, length / 96.2) : length < 8 ? .45 : 1
    this.viewIndex = 0
    this.forwardView.copy(forwardView ?? new Vector3(0, 4.8, length * .27))
    this.snapNextUpdate = true
    this.applyZoom()
    this.orbitOffset = 0
    this.heightFactor = 1
  }

  private applyZoom(): void {
    this.camera.near = this.isOnboard ? .15 : .5
    this.camera.updateProjectionMatrix()
    if (this.isOnboard) return
    const zoomIndex = this.viewIndex > 1 ? this.viewIndex - 1 : 0
    const zoom = this.largeVessel
      ? [{ distance: 145, height: 68 }, { distance: 210, height: 110 }, { distance: 330, height: 250 }, { distance: 480, height: 360 }][zoomIndex]
      : ChaseCamera.ZOOMS[zoomIndex]
    this.distance = zoom.distance * this.vesselScale
    this.height = zoom.height * this.vesselScale
  }

  /** Advance to the next zoom stop, wrapping back to close-in. */
  cycleZoom(): void {
    const wasOnboard = this.isOnboard
    this.viewIndex = (this.viewIndex + 1) % (ChaseCamera.ZOOMS.length + 1)
    // A cut avoids flying through the hull between an exterior and onboard view.
    this.snapNextUpdate = wasOnboard || this.isOnboard
    this.orbitOffset = 0
    this.heightFactor = 1
    this.orbitHold = 0
    this.applyZoom()
  }

  private boomAngle = 0

  /** Extra boom yaw from the player's drag, radians. Decays once released. */
  private orbitOffset = 0
  /** Drag also tilts the boom: 1 is the default height, 0 skims the water. */
  private heightFactor = 1
  private dragging = false
  /** Seconds left before a released orbit starts easing back behind the boat. */
  private orbitHold = 0

  constructor(aspect: number, dragSurface?: HTMLElement) {
    this.camera = new PerspectiveCamera(55, aspect, 0.5, 9000)
    if (dragSurface) this.bindOrbitDrag(dragSurface)
  }

  /**
   * Left-drag orbits around the boat; release holds the view briefly, then
   * eases back to the chase position. The chase loop keeps running while you
   * orbit — the offset rides on top of the follow heading, so the boat never
   * leaves frame even mid-manoeuvre.
   */
  private bindOrbitDrag(surface: HTMLElement): void {
    let pointerId: number | null = null
    let lastX = 0
    let lastY = 0
    surface.addEventListener('pointerdown', (event) => {
      if (this.isOnboard) return
      if (event.button !== 0 || pointerId !== null) return
      event.preventDefault()
      pointerId = event.pointerId
      this.dragging = true
      lastX = event.clientX
      lastY = event.clientY
      surface.setPointerCapture(event.pointerId)
    })
    surface.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId || this.isOnboard) return
      this.orbitOffset -= ((event.clientX - lastX) / window.innerWidth) * Math.PI * 2.2
      this.heightFactor = Math.min(
        Math.max(this.heightFactor + ((event.clientY - lastY) / window.innerHeight) * 3, 0.15),
        2.6,
      )
      lastX = event.clientX
      lastY = event.clientY
    })
    const end = (): void => {
      if (!this.dragging) return
      this.dragging = false
      const released = pointerId
      pointerId = null
      if (released !== null && surface.hasPointerCapture(released)) surface.releasePointerCapture(released)
      this.orbitHold = 2.5
    }
    const endPointer = (event: PointerEvent): void => {
      if (event.pointerId === pointerId) end()
    }
    surface.addEventListener('pointerup', endPointer)
    surface.addEventListener('pointercancel', endPointer)
    surface.addEventListener('lostpointercapture', endPointer)
    window.addEventListener('blur', end)
    surface.ownerDocument.addEventListener('visibilitychange', () => {
      if (surface.ownerDocument.hidden) end()
    })
  }

  snapBehind(position: Vector3, yaw: number): void {
    this.boomAngle = yaw
    this.update(position, yaw, null, 1)
  }

  update(tugPosition: Vector3, tugYaw: number, towPosition: Vector3 | null, dt: number, elapsed = 0, hullRotation?: Quaternion): void {
    if (this.isOnboard) {
      _desired.copy(this.forwardView)
      if (hullRotation) _desired.applyQuaternion(hullRotation)
      else _desired.applyAxisAngle(this.camera.up, tugYaw)
      _desired.add(tugPosition)
      _desired.y = Math.max(_desired.y, sampleWaterHeight(_desired.x, _desired.z, elapsed) + .65)
      // Stay in the seat at speed and look up as the bow climbs a crest. Ease
      // the pitch for comfort, keeping roll level so small chop cannot whip
      // the horizon sideways. Tows never pull this view aft.
      this.camera.position.copy(_desired)
      _forward.set(0, 0, 1)
      if (hullRotation) _forward.applyQuaternion(hullRotation)
      const pitch = Math.atan2(_forward.y, Math.hypot(_forward.x, _forward.z)) - .035
      if (this.snapNextUpdate) this.onboardPitch = pitch
      else this.onboardPitch += (pitch - this.onboardPitch) * (1 - Math.exp(-6 * dt))
      _look.set(
        Math.sin(tugYaw) * Math.cos(this.onboardPitch),
        Math.sin(this.onboardPitch),
        Math.cos(tugYaw) * Math.cos(this.onboardPitch),
      ).multiplyScalar(100).add(_desired)
      this.camera.lookAt(_look)
      this.boomAngle = tugYaw
      this.snapNextUpdate = false
      return
    }
    // Shortest-path angular chase toward the boat's heading.
    let error = tugYaw - this.boomAngle
    error = Math.atan2(Math.sin(error), Math.cos(error))
    this.boomAngle += error * Math.min(1, this.headingLag * dt)

    // Player orbit rides on top; after the post-release hold it bleeds away.
    if (!this.dragging) {
      if (this.orbitHold > 0) this.orbitHold -= dt
      else {
        const ease = Math.min(1, dt * 1.6)
        this.orbitOffset -= this.orbitOffset * ease
        this.heightFactor += (1 - this.heightFactor) * ease
      }
    }

    const boom = this.boomAngle + this.orbitOffset
    _desired.set(
      tugPosition.x - Math.sin(boom) * this.distance,
      tugPosition.y + this.height * this.heightFactor,
      tugPosition.z - Math.cos(boom) * this.distance,
    )

    // Never let the lens dip into a wave.
    const water = sampleWaterHeight(_desired.x, _desired.z, elapsed)
    if (_desired.y < water + 3) _desired.y = water + 3

    if (this.snapNextUpdate) this.camera.position.copy(_desired)
    else this.camera.position.lerp(_desired, Math.min(1, dt * 4))
    this.snapNextUpdate = false

    _look.copy(tugPosition)
    _look.y += (this.largeVessel ? 11 : 4) * this.vesselScale
    if (towPosition) {
      // Weight the look point toward the barge without losing the tug.
      _look.lerp(towPosition, 0.3)
      _look.y += 2
    }
    this.camera.lookAt(_look)
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect
    this.camera.updateProjectionMatrix()
  }
}
